import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import wordmarkWhite from "@/assets/techtail-wordmark-pixel.png";
import { submitLead } from "@/lib/leadSubmit";
import { divisionFromPath, isAcceptableEmail, isValidPhone, type Division } from "@/lib/leadGuard";
import { countryNameEs, isCountryCode } from "@/lib/countries";
import { CountrySelect, HoneypotField } from "./LeadFields";
import { useLeadSuccess } from "@/hooks/useLeadSuccess";

const PixelArrow = () => (
  <svg viewBox="0 0 12 12" className="h-4 w-4" fill="currentColor" shapeRendering="crispEdges">
    <rect x="1" y="5" width="7" height="2" />
    <rect x="7" y="3" width="2" height="1" />
    <rect x="7" y="8" width="2" height="1" />
    <rect x="9" y="5" width="2" height="2" />
  </svg>
);

type Answers = {
  nombre: string;
  interes: string;
  procedencia: string;
  ciudad: string;
  acompanantes: string;
  empresa: string;
  cargo: string;
  email: string;
  telefono: string;
  pais: string;
  slot: string; // ISO
};

const emptyAnswers: Answers = {
  nombre: "",
  interes: "",
  procedencia: "",
  ciudad: "",
  acompanantes: "",
  empresa: "",
  cargo: "",
  email: "",
  telefono: "",
  pais: "",
  slot: "",
};


// Canonical (Spanish) values kept for backend storage; labels are translated for display.
const INTERESES = [
  "Fábrica y hardware",
  "Retail autónomo",
  "Almacén e industrial",
  "Entornos médicos",
  "Plataforma y datos",
  "Todo",
];
const INTERES_KEYS = ["fabrica", "retail", "almacen", "medicos", "plataforma", "todo"] as const;

const PROCEDENCIAS = ["Desde Barcelona", "Desde fuera de Barcelona"];
const PROCEDENCIA_KEYS = ["barcelona", "fuera"] as const;

const ACOMPANANTES = ["Voy solo", "Somos dos", "Somos entre tres y cinco", "Somos más de cinco"];
const ACOMPANANTE_KEYS = ["solo", "dos", "tresACinco", "masDeCinco"] as const;

const STEPS = [
  "intro",
  "nombre",
  "interes",
  "procedencia",
  "acompanantes",
  "empresa",
  "slot",
  "final",
] as const;
type StepId = (typeof STEPS)[number];

const MORNING_HOURS = [9, 11]; // 2h duration
const LATE_HOURS = [10.5]; // 10:30 for people travelling
const SLOT_DURATION_H = 2;
const MIN_LEAD_DAYS = 2;
const WEEKS_AHEAD = 5;

const buildSlots = (fromOutside: boolean) => {
  const hours = fromOutside ? LATE_HOURS.concat([12]) : MORNING_HOURS;
  const out: Date[] = [];
  const now = new Date();
  const first = new Date(now);
  first.setDate(first.getDate() + MIN_LEAD_DAYS);
  first.setHours(0, 0, 0, 0);

  for (let d = 0; d < WEEKS_AHEAD * 7; d++) {
    const day = new Date(first);
    day.setDate(first.getDate() + d);
    const wd = day.getDay(); // 2 mar, 3 mié, 4 jue
    if (wd < 2 || wd > 4) continue;
    hours.forEach((h) => {
      const slot = new Date(day);
      slot.setHours(Math.floor(h), (h % 1) * 60, 0, 0);
      if (slot.getTime() > now.getTime()) out.push(slot);
    });
  }
  return out;
};

const fmtDay = (d: Date) =>
  d.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });

const fmtTime = (d: Date) =>
  d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });

const slotLabel = (d: Date) => {
  const end = new Date(d.getTime() + SLOT_DURATION_H * 60 * 60 * 1000);
  return `${fmtTime(d)} – ${fmtTime(end)}`;
};

const OptionButton = ({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left border px-5 py-4 text-base md:text-lg transition-colors ${
      selected
        ? "border-brand-blue bg-brand-blue text-black"
        : "border-white/25 text-white hover:border-white hover:bg-white/5"
    }`}
  >
    {label}
  </button>
);

const inputClass =
  "w-full border-b border-white/30 bg-transparent py-3 text-xl md:text-2xl text-white placeholder:text-white/35 focus:border-brand-blue focus:outline-none";

const Question = ({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <div className="w-full max-w-3xl">
    <h2 className="font-serif text-2xl md:text-4xl leading-tight text-white">{title}</h2>
    {hint && <p className="mt-3 text-sm md:text-base text-white/50">{hint}</p>}
    <div className="mt-8 space-y-3">{children}</div>
  </div>
);


interface Props {
  open: boolean;
  onClose: () => void;
}

const VisitForm = ({ open, onClose }: Props) => {
  const onSuccess = useLeadSuccess();
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const [honeypot, setHoneypot] = useState("");
  const startedAt = useRef(0);
  const schema = useMemo(
    () =>
      z.object({
        nombre: z.string().trim().min(1).max(100),
        empresa: z.string().trim().min(1).max(100),
        cargo: z.string().trim().max(100),
        email: z.string().trim().email().max(255),
        telefono: z.string().trim().min(6).max(30),
      }),
    [],
  );
  const [a, setA] = useState<Answers>(emptyAnswers);
  const [index, setIndex] = useState(0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [booked, setBooked] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const step: StepId = STEPS[Math.min(index, STEPS.length - 1)];
  const total = STEPS.length - 2;

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    setA(emptyAnswers);
    setIndex(0);
    setError(null);
    setHoneypot("");
    startedAt.current = Date.now();
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (step !== "slot") return;
    let active = true;
    setLoadingSlots(true);
    supabase
      .from("visit_slot_taken")
      .select("slot_start")
      .then(({ data }) => {
        if (!active) return;
        const list = Array.isArray(data) ? data : [];
        setBooked(list.map((x) => new Date(x.slot_start).toISOString()));
        setLoadingSlots(false);
      });
    return () => {
      active = false;
    };
  }, [step]);

  const fromOutside = a.procedencia === "Desde fuera de Barcelona";

  const groupedSlots = useMemo(() => {
    const slots = buildSlots(fromOutside).filter(
      (s) => !booked.includes(s.toISOString()),
    );
    const map = new Map<string, Date[]>();
    slots.forEach((s) => {
      const key = s.toDateString();
      map.set(key, [...(map.get(key) ?? []), s]);
    });
    return Array.from(map.entries());
  }, [fromOutside, booked]);

  const next = () => setIndex((i) => Math.min(i + 1, STEPS.length - 1));
  const back = () => setIndex((i) => Math.max(i - 1, 0));
  const set = <K extends keyof Answers>(k: K, v: Answers[K]) => setA((p) => ({ ...p, [k]: v }));
  const pick = <K extends keyof Answers>(k: K, v: Answers[K]) => {
    set(k, v);
    setTimeout(next, 180);
  };

  const canContinue = () => {
    switch (step) {
      case "nombre":
        return a.nombre.trim().length > 0;
      case "procedencia":
        return a.procedencia !== "" && (!fromOutside || a.ciudad.trim().length > 0);
      case "empresa":
        return (
          a.empresa.trim().length > 0 &&
          isAcceptableEmail(a.email) &&
          isValidPhone(a.telefono) &&
          a.pais !== ""
        );
      case "slot":
        return a.slot !== "";
      default:
        return true;
    }
  };


  const submit = async () => {
    const schema = z.object({
      nombre: z.string().trim().min(1).max(120),
      empresa: z.string().trim().min(1).max(160),
      cargo: z.string().trim().max(160).optional().default(""),
      email: z.string().trim().max(200).refine(isAcceptableEmail),
      telefono: z.string().trim().max(40).refine(isValidPhone),
      pais: z.string().refine(isCountryCode),
    });
    const parsed = schema.safeParse({
      nombre: a.nombre,
      empresa: a.empresa,
      cargo: a.cargo,
      email: a.email,
      telefono: a.telefono,
      pais: a.pais,
    });
    if (!parsed.success || !a.slot) {
      setError(t('sFormsVisit.errors.checkData'));
      return;
    }
    setSending(true);
    setError(null);

    const division: Division =
      a.interes === "Almacén e industrial"
        ? "industrial"
        : a.interes === "Entornos médicos"
          ? "medical"
          : divisionFromPath(pathname);
    const slotDate = new Date(a.slot);
    const res = await submitLead({
      core: {
        nombre: parsed.data.nombre,
        empresa: parsed.data.empresa,
        email: parsed.data.email,
        telefono: parsed.data.telefono,
        pais: countryNameEs(parsed.data.pais),
        mensaje: [
          `VISITA A FÁBRICA — ${fmtDay(slotDate)} · ${slotLabel(slotDate)} (2 h)`,
          `Interés: ${a.interes}`,
          `Procedencia: ${a.procedencia}${a.ciudad.trim() ? ` — ${a.ciudad.trim()}` : ""}`,
          `Acompañantes: ${a.acompanantes}`,
          `Cargo: ${a.cargo || "—"}`,
          `Teléfono: ${a.telefono}`,
        ].join("\n"),
      },
      extras: {
        division,
        form_name: "visita",
        request_type: "visita",
        cargo: parsed.data.cargo || null,
        ciudad: a.ciudad.trim() || null,
        country_code: parsed.data.pais,
      },
      visitRow: {
        slot_start: a.slot,
        nombre: parsed.data.nombre,
        interes: a.interes,
        procedencia: a.ciudad.trim() ? `${a.procedencia} (${a.ciudad.trim()})` : a.procedencia,
        acompanantes: a.acompanantes,
        empresa: parsed.data.empresa,
        cargo: parsed.data.cargo,
        email: parsed.data.email,
        telefono: parsed.data.telefono,
      },
      lang: i18n.language,
      honeypot,
      startedAt: startedAt.current,
    });
    setSending(false);
    if (!res.ok) {
      setError(t(res.reason === "slot_taken" ? 'sFormsVisit.errors.slotTaken' : 'sFormsVisit.errors.sendFailed'));
      return;
    }
    onClose();
    onSuccess("visita", { slot: a.slot }, { division, request_type: "visita", spam: res.spam });
  };

  const stepNumber = STEPS.indexOf(step);
  const chosen = a.slot ? new Date(a.slot) : null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[200] bg-black flex flex-col"
          role="dialog"
          aria-modal="true"
        >
          <HoneypotField value={honeypot} onChange={setHoneypot} />
          <div className="flex items-center justify-between px-5 md:px-10 py-5">
            <div className="flex items-center gap-4">
              <img src={wordmarkWhite} alt="TECHTAIL" className="h-5 md:h-6 w-auto" />
              {step !== "intro" && step !== "final" && (
                <span className="font-dot text-[11px] md:text-xs uppercase tracking-widest text-brand-blue">
                  {`// ${String(stepNumber).padStart(2, "0")} / ${total}`}
                </span>
              )}
            </div>
            {step !== "intro" && (
              <button
                type="button"
                onClick={onClose}
                className="text-white/60 text-sm hover:text-white transition-colors"
              >
                {t('sFormsVisit.close')}
              </button>
            )}
          </div>

          <div className="h-px w-full bg-white/10">
            <div
              className="h-px bg-brand-blue transition-all duration-300"
              style={{ width: `${(stepNumber / (STEPS.length - 1)) * 100}%` }}
            />
          </div>

          <div className="flex-1 overflow-y-auto flex flex-col">
            <div className="container mx-auto px-5 md:px-10 py-8 md:py-12">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -18 }}
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  className="w-full"
                >
                  {step === "intro" && (
                    <div className="max-w-2xl">
                      <h2 className="font-serif text-3xl md:text-5xl leading-tight text-white">
                        {t('sFormsVisit.intro.title')}
                      </h2>
                      <p className="mt-5 text-base md:text-lg text-white/60 max-w-xl">
                        {t('sFormsVisit.intro.description')}
                      </p>
                      <button
                        type="button"
                        onClick={next}
                        className="mt-10 inline-flex items-center gap-3 rounded-none bg-brand-blue pl-6 pr-2 py-2 text-black text-base"
                        >
                        {t('sFormsVisit.intro.start')}
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-none bg-black text-brand-blue">
                          <PixelArrow />
                        </span>
                      </button>
                    </div>
                  )}

                  {step === "nombre" && (
                    <Question title={t('sFormsVisit.steps.nombre.title')}>
                      <input
                        autoFocus
                        value={a.nombre}
                        maxLength={100}
                        onChange={(e) => set("nombre", e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && canContinue() && next()}
                        placeholder={t('sFormsVisit.steps.nombre.placeholder')}
                        className={inputClass}
                      />
                    </Question>
                  )}

                  {step === "interes" && (
                    <Question
                      title={t('sFormsVisit.steps.interes.title', { name: a.nombre ? `, ${a.nombre.trim()}` : "" })}
                    >
                      {INTERESES.map((o, i) => (
                        <OptionButton
                          key={o}
                          label={t(`sFormsVisit.steps.interes.options.${INTERES_KEYS[i]}`)}
                          selected={a.interes === o}
                          onClick={() => pick("interes", o)}
                        />
                      ))}
                    </Question>
                  )}

                  {step === "procedencia" && (
                    <Question title={t('sFormsVisit.steps.procedencia.title')}>
                      {PROCEDENCIAS.map((o, i) => (
                        <OptionButton
                          key={o}
                          label={t(`sFormsVisit.steps.procedencia.options.${PROCEDENCIA_KEYS[i]}`)}
                          selected={a.procedencia === o}
                          onClick={() =>
                            o === "Desde fuera de Barcelona"
                              ? set("procedencia", o)
                              : (set("ciudad", ""), pick("procedencia", o))
                          }
                        />
                      ))}
                      {a.procedencia === "Desde fuera de Barcelona" && (
                        <div className="pt-4">
                          <label className="block text-xs uppercase tracking-widest text-white/50 mb-2">
                            {t('sFormsVisit.steps.procedencia.ciudadLabel')}
                          </label>
                          <input
                            autoFocus
                            value={a.ciudad}
                            maxLength={100}
                            onChange={(e) => set("ciudad", e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && canContinue() && next()}
                            placeholder={t('sFormsVisit.steps.procedencia.ciudadPlaceholder')}
                            className={inputClass}
                          />
                        </div>
                      )}
                    </Question>
                  )}


                  {step === "acompanantes" && (
                    <Question title={t('sFormsVisit.steps.acompanantes.title')}>
                      {ACOMPANANTES.map((o, i) => (
                        <OptionButton
                          key={o}
                          label={t(`sFormsVisit.steps.acompanantes.options.${ACOMPANANTE_KEYS[i]}`)}
                          selected={a.acompanantes === o}
                          onClick={() => pick("acompanantes", o)}
                        />
                      ))}
                    </Question>
                  )}

                  {step === "empresa" && (
                    <Question title={t('sFormsVisit.steps.empresa.title')}>
                      <input
                        autoFocus
                        value={a.empresa}
                        maxLength={100}
                        onChange={(e) => set("empresa", e.target.value)}
                        placeholder={t('sFormsVisit.steps.empresa.empresaPlaceholder')}
                        className={inputClass}
                      />
                      <input
                        value={a.cargo}
                        maxLength={100}
                        onChange={(e) => set("cargo", e.target.value)}
                        placeholder={t('sFormsVisit.steps.empresa.cargoPlaceholder')}
                        className={inputClass}
                      />
                      <input
                        type="email"
                        value={a.email}
                        maxLength={255}
                        onChange={(e) => set("email", e.target.value)}
                        placeholder={t('sFormsVisit.steps.empresa.emailPlaceholder')}
                        className={inputClass}
                      />
                      <input
                        type="tel"
                        value={a.telefono}
                        maxLength={30}
                        onChange={(e) => set("telefono", e.target.value)}
                        placeholder={t('sFormsVisit.steps.empresa.telefonoPlaceholder')}
                        className={inputClass}
                      />
                      <CountrySelect value={a.pais} onChange={(v) => set("pais", v)} className={`${inputClass} appearance-none rounded-none`} />
                    </Question>
                  )}

                  {step === "slot" && (
                    <div className="w-full max-w-4xl">
                      <h2 className="font-serif text-2xl md:text-4xl leading-tight text-white">
                        {t('sFormsVisit.steps.slot.title')}
                      </h2>
                      <p className="mt-3 text-sm md:text-base text-white/50">
                        {t('sFormsVisit.steps.slot.hint')}
                      </p>

                      {loadingSlots ? (
                        <p className="mt-10 text-white/40 text-sm">{t('sFormsVisit.steps.slot.loading')}</p>
                      ) : (
                        <div className="mt-8 space-y-5 max-h-[46vh] overflow-y-auto pr-1">
                          {groupedSlots.map(([key, slots]) => (
                            <div key={key}>
                              <p className="font-dot text-[11px] uppercase tracking-widest text-brand-blue">
                                {`// ${fmtDay(slots[0])}`}
                              </p>
                              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {slots.map((s) => {
                                  const iso = s.toISOString();
                                  const selected = a.slot === iso;
                                  return (
                                    <button
                                      key={iso}
                                      type="button"
                                      onClick={() => set("slot", iso)}
                                      className={`flex items-center justify-between border px-5 py-4 text-base transition-colors ${
                                        selected
                                          ? "border-brand-blue bg-brand-blue text-black"
                                          : "border-white/25 text-white hover:border-white hover:bg-white/5"
                                      }`}
                                    >
                                      <span>{slotLabel(s)}</span>
                                      <span
                                        className={`font-dot text-[10px] uppercase tracking-widest ${
                                          selected ? "text-black/70" : "text-white/40"
                                        }`}
                                      >
                                        {t('sFormsVisit.steps.slot.duration')}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <p className="mt-6 text-xs md:text-sm text-white/40">
                        {t('sFormsVisit.steps.slot.noneFitBefore')}{" "}
                        <a
                          href="mailto:hola@techtail.es"
                          className="underline underline-offset-4 hover:text-white"
                        >
                          hola@techtail.es
                        </a>{" "}
                        {t('sFormsVisit.steps.slot.noneFitAfter')}
                      </p>
                    </div>
                  )}

                  {step === "final" && (
                    <div className="max-w-2xl">
                      <h2 className="font-serif text-3xl md:text-5xl leading-tight text-white">
                        {t('sFormsVisit.final.title', { name: a.nombre ? `, ${a.nombre.trim()}` : "" })}
                      </h2>
                      {chosen && (
                        <p className="mt-5 text-base md:text-lg text-white/70">
                          {fmtDay(chosen)} · {slotLabel(chosen)} {t('sFormsVisit.final.hours')}
                        </p>
                      )}
                      <p className="mt-3 text-base text-white/50">
                        {t('sFormsVisit.final.confirmation')}
                      </p>
                      <button
                        type="button"
                        onClick={onClose}
                        className="mt-10 inline-flex items-center gap-3 rounded-none bg-brand-blue pl-6 pr-2 py-2 text-black text-base"
                      >
                        {t('sFormsVisit.close')}
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-none bg-black text-brand-blue">
                          <PixelArrow />
                        </span>
                      </button>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {step !== "intro" && step !== "final" && (
            <div className="px-5 md:px-10 pb-8 pt-2 flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={back}
                className="text-sm text-white/50 hover:text-white transition-colors"
              >
                {t('sFormsVisit.nav.back')}
              </button>
              <div className="flex items-center gap-4">
                {error && <span className="text-sm text-white/70">{error}</span>}
                <button
                  type="button"
                  disabled={!canContinue() || sending}
                  onClick={step === "slot" ? submit : next}
                  className="inline-flex items-center gap-3 rounded-none bg-brand-blue pl-6 pr-2 py-2 text-black text-sm md:text-base disabled:opacity-40"
                >
                  {step === "slot" ? (sending ? t('sFormsVisit.nav.reserving') : t('sFormsVisit.nav.reserveVisit')) : t('sFormsVisit.nav.continue')}
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-none bg-black text-brand-blue">
                    <PixelArrow />
                  </span>
                </button>
              </div>
            </div>
          )}
          {step === "intro" && (
            <div className="absolute inset-x-0 bottom-0 z-10 flex justify-center px-5 pb-[max(2rem,env(safe-area-inset-bottom))] pt-4">
              <button
                type="button"
                aria-label={t("sFormsVisit.close")}
                onClick={onClose}
                className="h-12 w-12 flex items-center justify-center border border-white/30 text-white hover:text-brand-blue transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-6 w-6"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>

            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default VisitForm;
